import { AddMobileServiceInput, AddMobileServiceMutation, MobileService, ServiceType } from "@/graphql/__generated__/graphql";
import { useAddMobileServiceMutationHook, useRemoveServiceMutationHook } from "@/hooks";
import { format } from "date-fns";
import { useCallback, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/**
 * Interface for the return value of the useUpdateSiteVisit hook.
 * @interface UseUpdateSiteVisitReturn
 * @property {boolean} isProcessing - Whether the update is in progress.
 * @property {Error | null} error - The error that occurred during the update.
 * @property {(_service: MobileService) => Promise<void>} triggerUpdate - The function to trigger the update.
 */
interface UseUpdateSiteVisitReturn {
  isProcessing: boolean;
  error: Error | null;
  triggerUpdate: (_service: MobileService) => Promise<void>;
}

/**
 * Custom hook for updating a site visit. It works by appending the new changes and recreating the service,
 * navigating to the new service and removing the old service.
 * @returns {Object} - Contains the triggerUpdate function, isProcessing state, error state, and newServiceId state.
 */
export function useUpdateSiteVisit(): UseUpdateSiteVisitReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const { tenderID } = useParams<{ tenderID: string }>();

  // Refs to prevent duplicate operations
  const isProcessingRef = useRef(false);

  // Mutations
  const { addMobileServiceMutation } = useAddMobileServiceMutationHook({ disableToast: true, disableRefetch: true });
  const { removeServiceMutation } = useRemoveServiceMutationHook(0, { disableToast: true });

  const triggerUpdate = useCallback(async (service: MobileService) => {
    // Check if already processing
    if (isProcessingRef.current) {
      return;
    }

    // Check if the service is valid and is only mobile service
    if (!service || service.type !== ServiceType.Mobile) {
      setError(new Error("Invalid service type"));
      return;
    }

    isProcessingRef.current = true;
    setIsProcessing(true);
    setError(null);

    const mobileService = service as MobileService;

    try {
      // Step 1: Add new mobile service with identical values
      const createdServiceId = await new Promise<number>((resolve, reject) => {
        addMobileServiceMutation({
          variables: {
            tenderId: Number(mobileService.tenderId),
            input: {
              additional: mobileService.additional,
              hourlyWage: mobileService.hourlyWage,
              hoursBriefingPerEmployee: mobileService.hoursBriefingPerEmployee,
              info: mobileService.info,
              name: mobileService.name,
              nonTariffAllowance: mobileService.nonTariffAllowance,
              operationalOrganizationId: mobileService.operationalOrganizationId,
              payGroup: mobileService.payGroup,
              siteId: mobileService.siteId,
              siteVisits: mobileService.siteVisits.map((visit) => ({
                duration: visit.duration,
                margin: visit.margin,
                type: visit.type,
                mondayDay: visit.mondayDay,
                mondayNight: visit.mondayNight,
                tuesdayDay: visit.tuesdayDay,
                tuesdayNight: visit.tuesdayNight,
                wednesdayDay: visit.wednesdayDay,
                wednesdayNight: visit.wednesdayNight,
                thursdayDay: visit.thursdayDay,
                thursdayNight: visit.thursdayNight,
                fridayDay: visit.fridayDay,
                fridayNight: visit.fridayNight,
                saturdayDay: visit.saturdayDay,
                saturdayNight: visit.saturdayNight,
                sundayDay: visit.sundayDay,
                sundayNight: visit.sundayNight,
                holidayDay: visit.holidayDay,
                holidayNight: visit.holidayNight,
                ...(visit.startDate && { startDate: typeof visit.startDate === "string" ? visit.startDate : format(new Date(visit.startDate), "yyyy-MM-dd") }),
                ...(visit.endDate && { endDate: typeof visit.endDate === "string" ? visit.endDate : format(new Date(visit.endDate), "yyyy-MM-dd") })
              })),
              travelTime: mobileService.travelTime
            } satisfies AddMobileServiceInput
          },
          onCompleted: (data: AddMobileServiceMutation) => {
            resolve(data.addMobileService.id);
          },
          onError: (error: Error) => reject(error)
        });
      });

      // Step 2: Navigate to the new service
      if (tenderID) {
        navigate(`/tender/${tenderID}/services/mobile/${createdServiceId}`);
      }

      // Step 3: Remove the old service
      await new Promise<void>((resolve, reject) => {
        removeServiceMutation(
          {
            serviceId: mobileService.id,
          },
          {
            onCompleted: () => {
              resolve();
            },
            onError: (error: Error) => reject(error),
          }
        );
      });

    } catch (error) {
      setError(error as Error);
      console.error("Error during service update:", error);
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  }, [addMobileServiceMutation, removeServiceMutation, navigate, tenderID]);

  return {
    isProcessing,
    error,
    triggerUpdate
  };
}
